import { useState } from "react";
import { AditamentoDiferenca, FieldGroupAditamento } from "./aditamentoRevisionModal";
import { Accordion, AccordionDetails, AccordionSummary, IconButton, Table, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import { SpecialFieldRender } from "./specialFieldRender";
import { SmartFieldEditor } from "./smartFieldDetector";
import { AccountTree, CalendarToday, Cancel, Edit, ExpandMore, FormatListNumbered, Save, Settings, TextFields, List as ListIcon, ArrowDropDown } from "@material-ui/icons";
import { SpecializedArrayRenderer } from "./listagemDiferencasArray";

export const formatFieldName = (fieldName: string): string => {
    return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
};

export const formatFieldValue = (value: string): string => {
    if (!value || value === 'null') {
        return 'N/A';
    }

    if (value.match(/\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/)) {
        try {
            const date = new Date(value);
            return date.toLocaleString('pt-BR');
        } catch {
            return value;
        }
    }

    if (value === 'true' || value === 'false') {
        return value === 'true' ? 'Sim' : 'Não';
    }

    return value;
};

export const detectFieldType = (diferenca: AditamentoDiferenca): string => {
  const { nomeCampo, caminhoCompleto, newField, oldField, enumValues } = diferenca;

  if (enumValues && enumValues.length > 0) {
    return 'enum';
  }

  if (nomeCampo.includes('.Count')) return 'array-count';
  if (caminhoCompleto.includes('[') && caminhoCompleto.includes(']')) {
    if (nomeCampo.toLowerCase().includes('data') ||
      nomeCampo.toLowerCase().includes('date') ||
      newField?.match(/\d{4}-\d{2}-\d{2}/) ||
      oldField?.match(/\d{4}-\d{2}-\d{2}/)) {
      return 'array-date';
    }

    if (nomeCampo.toLowerCase().includes('percentual') ||
      nomeCampo.toLowerCase().includes('percent')) {
      return 'array-percentage';
    }

    if (!isNaN(Number(newField)) || !isNaN(Number(oldField))) {
      return 'array-number';
    }

    return 'array-item';
  }

  if (newField?.includes('(') && newField?.includes(')') ||
    oldField?.includes('(') && oldField?.includes(')')) return 'complex-object';

  if (nomeCampo.toLowerCase().includes('data') ||
    nomeCampo.toLowerCase().includes('date') ||
    newField?.match(/\d{4}-\d{2}-\d{2}/) ||
    oldField?.match(/\d{4}-\d{2}-\d{2}/)) return 'date';

  if (newField?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) ||
    oldField?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)) return 'guid';

  if (nomeCampo.toLowerCase().includes('percentual') ||
    nomeCampo.toLowerCase().includes('percent')) return 'percentage';

  if (!isNaN(Number(newField)) || !isNaN(Number(oldField))) return 'number';

  return 'text';
};

export const getFieldIcon = (fieldType: string) => {
  switch (fieldType) {
    case 'array-count':
    case 'array-item':
    case 'array-date':
    case 'array-number':
    case 'array-percentage':
      return <ListIcon className="field-type-icon" />;
    case 'complex-object':
      return <Settings className="field-type-icon" />;
    case 'date':
    case 'array-date':
      return <CalendarToday className="field-type-icon" />;
    case 'number':
    case 'percentage':
    case 'array-number':
    case 'array-percentage':
      return <FormatListNumbered className="field-type-icon" />;
    case 'guid':
      return <AccountTree className="field-type-icon" />;
    case 'enum':
      return <ArrowDropDown className="field-type-icon" />;
    default:
      return <TextFields className="field-type-icon" />;
  }
};

export const getArrayType = (basePath: string): 'amortizacoes' | 'pagamentojuros' | 'incorporacaojuros' | 'generic' => {
  const lowerPath = basePath.toLowerCase();

  if (lowerPath === 'amortizacoes') return 'amortizacoes';
  if (lowerPath === 'pagamentojuros') return 'pagamentojuros';
  if (lowerPath === 'incorporacaojuros') return 'incorporacaojuros';

  if (lowerPath.includes('amortizac')) return 'amortizacoes';
  if (lowerPath.includes('pagamentojuros') || lowerPath.includes('pagamento')) return 'pagamentojuros';
  if (lowerPath.includes('incorporacaojuros') || lowerPath.includes('incorporacao')) return 'incorporacaojuros';

  return 'generic';
};

export const ListagemDiferencas: React.FC<{
    group: FieldGroupAditamento;
    canEdit: boolean;
    editingField: string | null;
    classes: any;
    onEditField: (path: string) => void;
    onSaveField: () => void;
    onCancelEdit: () => void;
    onFieldValueChange: (path: string, value: string) => void;
    onDifferencesUpdate?: (updatedDifferences: AditamentoDiferenca[]) => void;
    allDifferences: AditamentoDiferenca[];
    isDivergenciasMode?: boolean;
}> = ({
    group,
    canEdit,
    editingField,
    classes,
    onEditField,
    onSaveField,
    onCancelEdit,
    onFieldValueChange,
    onDifferencesUpdate,
    allDifferences,
    isDivergenciasMode = false,
}) => {
        const [expanded, setExpanded] = useState(group.isExpanded || group.groupType === 'simple');

        if (group.groupType === 'simple') {
            const diferenca = group.fields[0];
            const fieldType = detectFieldType(diferenca);

            return (
                <TableRow key={diferenca.caminhoCompleto}>
                    <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {getFieldIcon(fieldType)}
                            <div>
                                <Typography className={classes.fieldName}>
                                    {formatFieldName(diferenca.nomeCampo)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {diferenca.tipoClasse}
                                </Typography>
                                {!isDivergenciasMode && diferenca.caminhoCompleto !== diferenca.nomeCampo && (
                                    <div className={classes.pathBreadcrumb}>
                                        {diferenca.caminhoCompleto}
                                    </div>
                                )}
                            </div>
                        </div>
                    </TableCell>
                    <TableCell>
                        <SpecialFieldRender
                            value={diferenca.oldField}
                            fieldType={fieldType}
                            isOldValue={true}
                            classes={classes}
                        />
                    </TableCell>
                    <TableCell>
                        <SmartFieldEditor
                            diferenca={diferenca}
                            fieldType={fieldType}
                            isEditing={editingField === diferenca.caminhoCompleto}
                            onValueChange={(value) => onFieldValueChange(diferenca.caminhoCompleto, value)}
                            classes={classes}
                        />
                    </TableCell>
                    {canEdit && (
                        <TableCell align="center">
                            <div className={classes.actionButtons}>
                                {editingField === diferenca.caminhoCompleto ? (
                                    <>
                                        <IconButton size="small" onClick={onSaveField} color="primary">
                                            <Save fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={onCancelEdit}>
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <IconButton
                                        size="small"
                                        onClick={() => onEditField(diferenca.caminhoCompleto)}
                                        color="primary"
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                )}
                            </div>
                        </TableCell>
                    )}
                </TableRow>
            );
        }

        const useSpecializedRenderer = group.groupType === 'array' &&
            group.arrayType &&
            group.arrayType !== 'generic' &&
            ['amortizacoes', 'pagamentojuros', 'incorporacaojuros'].includes(group.arrayType);

        return (
            <TableRow>
                <TableCell colSpan={canEdit ? 4 : 3} style={{ padding: 0 }}>
                    <Accordion
                        expanded={expanded}
                        onChange={() => setExpanded(!expanded)}
                        className={classes.groupAccordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <div className={classes.groupTitle}>
                                {group.icon}
                                <Typography variant="subtitle1">
                                    {group.groupName}
                                    <span className={classes.arrayBadge}>
                                        {group.fields.length} {group.fields.length === 1 ? 'campo' : 'campos'}
                                    </span>
                                </Typography>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails>
                            {useSpecializedRenderer ? (
                                <SpecializedArrayRenderer
                                    group={group}
                                    canEdit={canEdit}
                                    onFieldValueChange={onFieldValueChange}
                                    onDifferencesUpdate={onDifferencesUpdate}
                                    allDifferences={allDifferences}
                                    classes={classes}
                                />
                            ) : (
                                <Table size="small">
                                    <TableBody>
                                        {group.fields.map((diferenca, index) => {
                                            const fieldType = detectFieldType(diferenca);
                                            return (
                                                <TableRow key={`${diferenca.caminhoCompleto}-${index}`}>
                                                    <TableCell style={{ width: '30%' }}>
                                                        <div className={classes.nestedField}>
                                                            <Typography className={classes.fieldName}>
                                                                {formatFieldName(diferenca.nomeCampo)}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {diferenca.tipoClasse}
                                                            </Typography>
                                                            {!isDivergenciasMode && (
                                                                <div className={classes.pathBreadcrumb}>
                                                                    {diferenca.caminhoCompleto}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell style={{ width: '30%' }}>
                                                        <SpecialFieldRender
                                                            value={diferenca.oldField}
                                                            fieldType={fieldType}
                                                            isOldValue={true}
                                                            classes={classes}
                                                        />
                                                    </TableCell>
                                                    <TableCell style={{ width: canEdit ? '30%' : '40%' }}>
                                                        <SmartFieldEditor
                                                            diferenca={diferenca}
                                                            fieldType={fieldType}
                                                            isEditing={editingField === diferenca.caminhoCompleto}
                                                            onValueChange={(value) => onFieldValueChange(diferenca.caminhoCompleto, value)}
                                                            classes={classes}
                                                        />
                                                    </TableCell>
                                                    {canEdit && (
                                                        <TableCell align="center" style={{ width: '10%' }}>
                                                            <div className={classes.actionButtons}>
                                                                {editingField === diferenca.caminhoCompleto ? (
                                                                    <>
                                                                        <IconButton size="small" onClick={onSaveField} color="primary">
                                                                            <Save fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton size="small" onClick={onCancelEdit}>
                                                                            <Cancel fontSize="small" />
                                                                        </IconButton>
                                                                    </>
                                                                ) : (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => onEditField(diferenca.caminhoCompleto)}
                                                                        color="primary"
                                                                    >
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </TableCell>
            </TableRow>
        );
    };